"use client";

import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Heart, Users, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export default function AboutUs() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const imageRef = useRef<HTMLDivElement>(null);
  const [tiltStyle, setTiltStyle] = useState({});
  useEffect(() => {
    if (!imageRef.current) return;
    const handleScroll = () => {
      if (!imageRef.current) return;
      const rect = imageRef.current.getBoundingClientRect();
      const scrollProgress = Math.max(0, Math.min(1, 1 - rect.top / window.innerHeight));
      const tiltY = scrollProgress * 6 - 3;
      const tiltX = scrollProgress * 4 - 2;
      setTiltStyle({
        transform: `perspective(1000px) rotateY(${tiltY}deg) rotateX(${tiltX}deg)`
      });
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const icons = [{
    Icon: Heart,
    title: "Страст към качеството",
    description: "Подборът на вискококачесвени продукти е наш топ приоритет",
    bg: "bg-[hsl(15_85%_95%)]",
    iconColor: "text-primary"
  }, {
    Icon: Users,
    title: "Място за споделени моменти",
    description: "Перфектно за рожденни дни, годишнини, фирмени събития и множество други поводи\xA0",
    bg: "bg-[hsl(95_50%_95%)]",
    iconColor: "text-secondary"
  }, {
    Icon: Sparkles,
    title: "Иновация, динамика, топ цени",
    description: "Всеки ден ново меню, нови вкусове, нови усмивки. Цените - едни от най-добрите за типа кухня. Защото вярваме, че вкусното не означава скъпо",
    bg: "bg-[hsl(210_80%_95%)]",
    iconColor: "text-accent"
  }];
  return <section id="за-нас" ref={sectionRef} className="relative bg-cover bg-center bg-no-repeat bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1504672/img/img_d20776159488780bf121e28e31704065_v.png)] text-foreground py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-background/90 pointer-events-none" />
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {}
          <div ref={imageRef} className={`relative transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100" : "opacity-0"}`} style={tiltStyle}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-clay">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none z-10" />
              <img src="https://first-ladybird.10web.cloud/wp-content/uploads/2026/02/IMG-6ce3c7fc10e2caa4da36f325072c2724-V.webp" alt="Интериор на Миш-Маш" className="w-full h-auto object-cover aspect-[4/3]" />
            </div>
          </div>

          {}
          <div className="space-y-8">
            <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "animate-reveal" : "opacity-0"}`} style={{
            animationDelay: "100ms"
          }}>
              <h2 className="font-display font-bold text-[32px] leading-[1.25] mb-4 text-foreground">
                Нашата история
              </h2>
              <p className="text-[16px] leading-[1.5] text-muted-foreground mb-6">Миш-Маш /интерпред/ e повече от заведение за бързо хранене — това е място, където всеки ден създаваме нови вкусови преживявания. Нашето динамично меню се обновява ежедневно, за да предложим на гостите си разнообразие и свежест. С над 6 години професионален опит в кухнята, гарантираме качеството на всяко наше ястие.<br /><br />Собственикът не само управлява заведението – той е в кухнята всеки ден, готви рамо до рамо с екипа и лично контролира качеството на всяко ястие. Защото за нас храната не е просто храна – тя е страст и обещание към всеки гост.</p>
              <p className="text-[16px] leading-[1.5] text-muted-foreground">Вярваме в енергията, приятелството и модерния подход към храненето. Независимо дали търсите бърз обяд или място за организиране на събитие, ние сме тук, за да ви посрещнем с усмивка. Работим от понеделник до петък (с изключение на резервации за събития, направени за събота и неделя).</p>
            </div>

            {}
            <div className="space-y-5">
              {icons.map((item, i) => <Card key={i} data-index={i} className={`card p-5 flex items-start gap-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${isVisible ? "animate-reveal" : "opacity-0"} hover:bg-[hsl(0_0%_97%)]`} style={{
              animationDelay: `${200 + i * 100}ms`
            }}>
                  <div className={`${item.bg} ${item.iconColor} p-3 rounded-lg shadow-soft flex-shrink-0 transition-transform duration-[var(--duration-fast)] ease-[var(--ease-spring)] hover:scale-110`}>
                    <item.Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-[18px] mb-1 text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-[14px] leading-[1.4] text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}